<template>
  <tabs />
  <toolbar class="toolbar">
    <filter-pane v-model:page="page" v-model="filter" :filters="filters" />
    <add-fields-pane v-model="visibleColumns" :fields="columns" />
    <action
      :icon="AddIcon"
      :label="$t('pages.campaigns.statisticPointsList.modal.new.buttonText')"
      @click="editStatisticPoint = null; openStatisticPointModal = true"
    />
    <export
      url="/statistic_points/csv"
      :filter="totalFilter"
      :columns="columns"
      :visible-columns="visibleColumns"
      :title="csvTitle"
      :sort-key="sortKey"
      :sort-direction="sortDirection"
    />
  </toolbar>
  <statistic-point-modal
    v-model:open="openStatisticPointModal"
    :campaign-group="modelValue"
    :statistic-point="editStatisticPoint"
    @saved="refetch"
  />
  <div class="main-area h-full">
    <statistic-points-map
      class="ml-12"
      v-if="records?.length"
      :busy="isLoading"
      :points="pointsForMap"
    />

    <div class="ml-12">
      <div class="text-body-regular-bold text-table-text-gray uppercase mb-5" data-v-67ba7ed1="">
        {{ $t('pages.campaigns.statisticPointsList.tableHeader') }}
      </div>

      <div class="table">
        <data-table
          class="mb-auto"
          v-model:sort-by="sortKey"
          v-model:sort-direction="sortDirection"
          :busy="isLoading"
          :items="records"
          :columns="columns"
          :visible-columns="visibleColumns"
          :actions="actions"
          :choosable="true"
          v-model:checked-ids-list="checkedIdsList"
        />
      </div>
    </div>
  </div>
  <pagination v-model:page="page" v-model:per-page="perPage" class="pagination" :total="total" />
</template>

<script lang="tsx" setup>
import {computed, inject, reactive, ref, type Ref, unref, watch} from 'vue';
import AddIcon from '@images/panes/add.svg?component';
import Action from '@utils/toolbar/Action.vue';
import {useI18n} from 'vue-i18n';
import FilterPane from '@components/utils/toolbar/panes/Filter.vue';
import AddFieldsPane from '@components/utils/toolbar/panes/AddFields.vue';
import Toolbar from '@components/utils/toolbar/Toolbar.vue';
import Pagination from '@components/utils/table/Pagination.vue';
import type {Action as ActionType} from '@components/utils/table/Table.vue';
import DataTable, {type Column} from '@components/utils/table/Table.vue';
import {useProvider} from '@components/utils/table/hooks/provider';
import type {Filter} from '@utils/filter/Filter.vue';
import type {CampaignGroup, HydraCollection, StatisticPoint} from '@/entities/Entities';
import Tabs from '@components/structure/navigation/Tabs.vue';
import {useDelete} from '@hooks/api';

import Export from '@utils/toolbar/panes/Export.vue';
import {format} from 'date-fns';
import StatisticPointsMap from '@pages/campaigns/campaignGroup/StatisticPointsMap.vue';
import StatisticPointModal from '@pages/campaigns/campaignGroup/StatisticPointModal.vue';
import {formatTimeDurationFromSeconds} from '@/utils/helpers';
import {useQueryRef} from '@/hooks/url';
import {useTableFields} from '@hooks/table-configuration';
import globalFilterKey from '@utils/globalFilter/injects';

interface Props {
  modelValue: CampaignGroup;
}

const props = defineProps<Props>();

const campaignGroupNeedsLoading = computed(() => {
  return !props.modelValue;
});

const globalFilter = inject(globalFilterKey) as unknown as Ref<{ regions: {label: string, value: string}[] }>;

const page = useQueryRef('page', 1);
const perPage = useQueryRef('perPage', 100);

const openStatisticPointModal = ref(false);
const editStatisticPoint = ref();

const sortKey = useQueryRef<string | undefined>('sortKey', undefined);
const sortDirection = useQueryRef<'ASC' | 'DESC' | undefined>('sortDirection', undefined);

const { t } = useI18n();

const csvTitle = computed(() => {
  return t('csv.title.statisticPointsFromGroup', {
    group: props.modelValue?.name,
  });
});

const filter = useQueryRef('filter', {});
const fixedFilter = computed(() => ({
  'campaign.group': props.modelValue?.id ?? '',
  'inArray[campaign.region]': globalFilter?.value?.regions,
}));
const totalFilter = computed(() => {
  return { ...fixedFilter.value, ...filter.value };
});

const providerParams = reactive({
  sort: {
    sortKey,
    sortDirection,
  },
  filter: totalFilter,
  pagination: {
    page,
    perPage,
  },
});

const [deleteStatisticPoint, { loading: deleting }] = useDelete();

const actions: ActionType[] = [
  {
    title: t('pages.campaigns.statisticPointsList.actions.edit'),
    action: (item: StatisticPoint) => {
      editStatisticPoint.value = { ...item, campaign: item.campaign?.['@id'] };
      openStatisticPointModal.value = true;
    },
  },
  {
    title: t('pages.campaigns.statisticPointsList.actions.delete'),
    action: async (item: StatisticPoint) => {
      if (await deleteStatisticPoint(`/statistic_points/${item.id}`)) {
        // eslint-disable-next-line no-use-before-define
        await refetch();
      }
    },
  },
];

type ColumnFilter = Partial<Filter> & Omit<Filter, 'key'>;
type ColumnDefinition = Column & { filter?: ColumnFilter | ColumnFilter[] } & { group: string };

const columns: ColumnDefinition[] = [
  {
    key: 'id',
    title: t('id'),
    sortable: true,
    group: t('pages.campaigns.statisticPointsList.fieldGroups.general'),
    class: 'whitespace-nowrap',
    filter: {
      type: 'text',
      group: t('pages.campaigns.statisticPointsList.filterGroups.general'),
      options: {
        label: t('id'),
      },
    },
  },
  {
    key: 'name',
    title: t('statisticPoint.name'),
    sortable: true,
    visible: true,
    group: t('pages.campaigns.statisticPointsList.fieldGroups.general'),
    filter: {
      type: 'text',
      group: t('pages.campaigns.statisticPointsList.filterGroups.general'),
      options: {
        label: t('statisticPoint.name'),
      },
    },
  },
  {
    key: 'campaign.name',
    title: t('campaign.DEFAULT'),
    group: t('pages.campaigns.statisticPointsList.fieldGroups.general'),
    sortable: true,
    visible: true,
    filter: {
      key: 'campaign',
      type: 'dropdown',
      group: t('pages.campaigns.statisticPointsList.filterGroups.general'),
      options: {
        options: computed(() =>
          props.modelValue?.campaigns?.map((value) => ({
            label: value.name,
            value: value.id,
          }))
        ),
        label: t('campaign.DEFAULT'),
      },
    },
  },
  {
    key: 'campaign.region.name',
    title: t('campaign.region'),
    group: t('pages.campaigns.statisticPointsList.fieldGroups.general'),
    sortable: true,
    visible: true,
  },
  {
    key: 'campaign.timeStatus',
    csvTranslations: {
      ongoing: t('campaign.timeStatus.ongoing'),
      future: t('campaign.timeStatus.future'),
      past: t('campaign.timeStatus.past'),
    },
    title: t('campaign.timeStatus.DEFAULT2'),
    group: t('pages.campaigns.statisticPointsList.fieldGroups.general'),
    sortable: false,
    visible: true,
    computed: (sp: StatisticPoint) => {
      const now = new Date();
      if (!sp.campaign) {
        return '';
      }
      const startDate = new Date(sp.campaign.actualStartDate);
      const endDate = new Date(sp.campaign.actualEndDate);
      if(startDate <= now && endDate >= now) {
        return t('campaign.timeStatus.ongoing');
      } else if(startDate > now) {
        return t('campaign.timeStatus.future');
      } else {
        return t('campaign.timeStatus.past');
      }
    },
    filter: {
      key: 'timeStatus',
      type: 'dropdown',
      group: t('pages.campaigns.statisticPointsList.filterGroups.general'),
      options: {
        options: [
          {
            label: t('campaign.timeStatus.past'),
            value: 'past',
          },
          {
            label: t('campaign.timeStatus.ongoing'),
            value: 'ongoing',
          },
          {
            label: t('campaign.timeStatus.future'),
            value: 'future',
          },
        ],
        label: t('campaign.timeStatus.DEFAULT2'),
      },
    },
  },
  {
    key: 'radius',
    title: t('statisticPoint.radius'),
    sortable: true,
    visible: true,
    group: t('pages.campaigns.statisticPointsList.fieldGroups.general'),
    filter: {
      type: 'range',
      group: t('pages.campaigns.statisticPointsList.filterGroups.general'),
      options: {
        icon: <span>m</span>,
        label: t('statisticPoint.radius'),
      },
    },
  },
  {
    key: 'gisLocation.lat',
    title: t('statisticPoint.gisLocation.latitude'),
    sortable: false,
    visible: true,
    group: t('pages.campaigns.statisticPointsList.fieldGroups.general'),
  },
  {
    key: 'gisLocation.long',
    title: t('statisticPoint.gisLocation.longitude'),
    sortable: false,
    visible: true,
    group: t('pages.campaigns.statisticPointsList.fieldGroups.general'),
  },
  {
    key: 'validityStart',
    title: t('statisticPoint.validityStart'),
    sortable: true,
    visible: false,
    formatter: (v: string) => (v ? format(new Date(v), 'dd.MM.yyyy - HH:mm:ss') : '-'),
    group: t('pages.campaigns.statisticPointsList.filterGroups.general'),
    filter: {
      type: 'datetime',
      group: t('pages.campaigns.statisticPointsList.filterGroups.general'),
      options: {
        label: t('statisticPoint.validityStart'),
      },
    },
  },
  {
    key: 'validityEnd',
    title: t('statisticPoint.validityEnd'),
    sortable: true,
    visible: false,
    formatter: (v: string) => (v ? format(new Date(v), 'dd.MM.yyyy - HH:mm:ss') : '-'),
    group: t('pages.campaigns.statisticPointsList.filterGroups.general'),
    filter: {
      type: 'datetime',
      group: t('pages.campaigns.statisticPointsList.filterGroups.general'),
      options: {
        label: t('statisticPoint.validityEnd'),
      },
    },
  },
  {
    key: 'statistics.stops',
    title: t('statisticPoint.stops'),
    group: t('pages.campaigns.statisticPointsList.fieldGroups.statistics'),
    visible: true,
    sortable: false,
    filter: {
      type: 'range',
      group: t('pages.campaigns.statisticPointsList.filterGroups.statistics'),
      options: {
        label: t('statisticPoint.stops'),
      },
    },
  },
  {
    key: 'statistics.time',
    title: t('statisticPoint.duration'),
    group: t('pages.campaigns.statisticPointsList.fieldGroups.statistics'),
    formatter: (v: number) => (v ? formatTimeDurationFromSeconds(v) : '00:00:00'),
    visible: true,
    sortable: false,
    filter: {
      type: 'duration',
      group: t('pages.campaigns.statisticPointsList.filterGroups.statistics'),
      options: {
        placeholderFrom: 'hh:mm:ss',
        placeholderTo: 'hh:mm:ss',
        label: t('statisticPoint.duration'),
      },
    },
  },
];

const visibleColumns = useTableFields(
    columns,
);

const {
  data: items,
  loading,
  refetch,
} = useProvider<HydraCollection<StatisticPoint>>('/statistic_points', providerParams, {
  disabled: campaignGroupNeedsLoading,
});

const records = computed(() => unref(items)?.['hydra:member']);
const total = computed(() => unref(items)?.['hydra:totalItems']);
const filters = computed(() =>
  columns.flatMap((col): Filter[] => {
    if (!col.filter) return [];
    if (Array.isArray(col.filter))
      return col.filter.map((f) => ({
        key: col.key,
        ...f,
      }));
    return [{ key: col.key, ...col.filter }];
  })
);

const isLoading = computed(() => loading.value || deleting.value);

const checkedIdsList = ref<string[]>([]);
const pointsForMap = computed(() => {
  return checkedIdsList.value.map((id) => {
    return records.value?.find((record) => record.id === id);
  });
});

watch(
  () => loading.value,
  (newVal, oldVal) => {
    if (!newVal && oldVal) {
      checkedIdsList.value = records.value?.map((item) => item.id) ?? [];
    }
  }
);
</script>

<style lang="scss" scoped>
button {
  @apply block;
}

.table {
  @apply flex flex-col;
}
</style>

